import { forwardRef, memo, Ref, useCallback, useMemo } from 'react';
import { ProfessionalProfessions } from '~/constants';

import { Value } from '../Field';
import { Radio } from '../Radio';
import { IInputJobProps } from '../Types';
import { getProfessionName } from './Utils';
import { getShouldOfficeShowEfda } from '~/growthbook';
import { getAllProfessionTypeOptions } from '~/utils/DentistUtils';
import { SolidUserIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';

const Component = (
  {
    defaultValue,
    isDisabled = false,
    isOpen = false,
    value: controlledValue,
    onChange,
    onToggle,
  }: IInputJobProps<string>,
  ref: Ref<HTMLDivElement>,
) => {
  const options = useMemo(() => {
    const isShowEFDA = getShouldOfficeShowEfda();

    return getAllProfessionTypeOptions(ProfessionalProfessions, {
      showEfda: isShowEFDA,
    }).map((it) => ({
      label: it.name,
      value: it.code,
    }));
  }, []);

  const CustomValueComponent = useCallback(
    (currentValue: string | undefined) => {
      return <Value text={getProfessionName(options, currentValue)} />;
    },
    [options],
  );

  return (
    <Radio
      IconSrc={<SolidUserIcon size={24} color={colors.secondary[500]} />}
      ValueComponent={CustomValueComponent}
      defaultValue={defaultValue}
      isDisabled={isDisabled}
      isOpen={isOpen}
      value={controlledValue}
      onChange={onChange}
      onToggle={onToggle}
      options={options}
      title="Professional Type:"
      testName="input-job.professional"
      ref={ref}
    />
  );
};

/**
 * Professional selection component for job input forms.
 *
 * A specialized radio field component that allows users to select from predefined
 * professional types. Built on top of the Radio component with profession-specific
 * configurations and data transformation.
 *
 * Features:
 * - Pre-configured with dental professional options
 * - User icon for visual identification
 * - Automatic profession name mapping via utility function
 * - Inherits all Radio component functionality (collapsible, controlled/uncontrolled)
 *
 * @example
 * ```tsx
 * <Professional
 *   value="dentist"
 *   onChange={(profession) => setProfession(profession)}
 * />
 * ```
 */
export const Professional = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: IInputJobProps<string> & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
